import CheckmarkOutline16 from '@carbon/icons-react/es/checkmark--outline/16';
import Copy16 from '@carbon/icons-react/es/copy/16';
import { faTelescope } from '@fortawesome/pro-duotone-svg-icons';
import { Tooltip, Typography } from 'antd';
import { isEmpty, keyBy } from 'lodash';
import { parse } from 'query-string';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { matchPath, useLocation } from 'react-router';
import styled from 'styled-components';
import { FormAnswer, FormQuestion, FormResponseType } from 'api/form';
import { Alert } from 'components/atoms/alert';
import { Button } from 'components/atoms/button';
import {
  EmptyState,
  EmptyStateContainer as EmptyStateContainerAtom,
  EmptyStateIcon,
} from 'components/atoms/empty-state';
import { Loading } from 'components/atoms/loading';
import { FormSection } from 'components/molecules/forms/form-fields/styles';
import { ToastDescription, toastService } from 'components/molecules/toast';
import { FormQuestionRenderer } from 'components/organisms/forms';
import { LastEditedBy } from 'components/organisms/forms/form-components';
import {
  computeFormAnswerPayload,
  computeFormDefaultValues,
  FormResponse,
  resetQuestionsToEmptyValuesUtil,
  saveFormRespToIndexedDb,
  setValuesInForm,
  validateAttachmentUploading,
} from 'components/organisms/forms/utils';
import { RbacButton } from 'components/organisms/rbac/components/rbac-buttons';
import {
  TASK_CONVERSATION_PANE_WIDTH,
  TASK_DRAWER_WIDTH,
} from 'components/organisms/tasks/constants';
import { flattenQuestions } from 'components/organisms/templates/forms/utils';
import { BodyShort01 } from 'components/styles';
import { RoutePaths } from 'constant';
import { useProjectAsRBACResource } from 'hooks/rbac/useWithAccess';
import { useDeleteConfirmModal } from 'hooks/use-delete-confirm-modal';
import { useFormUnload } from 'hooks/use-form-unload';
import {
  deleteFormResponseFromIndexedDb,
  useGetUserFromIndexedDb,
} from 'hooks/use-idb';
import { useTranslate } from 'hooks/use-translation';
import { useIsNativeUser } from 'hooks/use-user-type';
import {
  getQueryAllAnswersByFormInstanceKey,
  getQueryAllAnswersByFormTemplateKey,
  useCreateUpdateFormAnswer,
  useFormAnswerQuery,
  useQueryFormInstanceById,
} from 'queries/form-instance';
import { formAnswerSubmissionFailureToast } from './utils';

const TASK_DETAIL_PANE_WIDTH = TASK_DRAWER_WIDTH - TASK_CONVERSATION_PANE_WIDTH;

const EmptyStateContainer = styled(EmptyStateContainerAtom)`
  && {
    text-align: left;
    padding: var(--spacing-11) 0px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const FormSharableWrapper = styled.div`
  background: var(--coolest-gray-10);
  padding: 0 var(--spacing-05);
  display: flex;
  align-items: center;
`;
const FormSharableLink = styled.div`
  ${BodyShort01};
  .ant-typography {
    color: var(--gray-70);
  }
`;
const FormLinkCopyButton = styled.div`
  margin-left: auto;
`;

const FormWrapper = styled.div`
  overflow: hidden;
  overflow-y: auto;
  padding: 0 var(--layout-03) var(--spacing-11);
  background-color: var(--gray-10);
  height: 100%;
`;
const FormDesc = styled.div`
  ${BodyShort01};
  color: var(--gray-80);
  padding: var(--spacing-07) 0 0;
`;
const FormElements = styled.div`
  padding-top: var(--spacing-07);
  section {
    ${FormSection};
    margin-bottom: var(--spacing-05);
  }
`;
const FormFooterWrapper = styled.div`
  height: 112px;
  position: fixed;
  width: ${TASK_DETAIL_PANE_WIDTH}px;
  bottom: 0;
  z-index: var(--zindex-elevated);
`;
const FormFooterActionsWrapper = styled.div`
  height: 72px;
  background: var(--white-100);
  border-top: 1px solid var(--border-color);
  padding: var(--spacing-05) var(--layout-03);
  display: flex;
  align-items: center;
`;

const StyledAlert = styled(Alert)<{ showBanner: boolean }>`
  border: none;
  height: 40px;
  padding: var(--spacing-03) var(--spacing-05);
  align-items: center;
  opacity: ${(props) => (props.showBanner ? '1' : '0')};
  transition: opacity 0.4s ease-in-out;
`;
const NoResponseText = styled.div`
  color: var(--gray-50);
`;

const FooterLeftGroup = styled.div`
  display: flex;
`;
const FooterRightGroup = styled.div`
  display: flex;
  margin-left: auto;
  gap: var(--spacing-04);
`;

export const TaskFormRenderer = ({
  projectId,
  taskId,
  formResponseCount,
  instanceId,
  responseId,
}: {
  projectId: number;
  taskId: number;
  formResponseCount: number | undefined;
  instanceId: number;
  responseId: number | undefined;
}): JSX.Element => {
  const { hash, pathname } = useLocation();
  const hashValue = parse(hash);
  const formResponseQp = (hashValue.form_response_id as string) || '';
  const isFormResponseEditMode = !isEmpty(formResponseQp);
  const [formResponseId, setFormResponseId] = useState(
    formResponseQp ? parseInt(formResponseQp, 10) : responseId,
  );
  const [renderRowError, setRenderRowError] = useState({});
  const queryClient = useQueryClient();
  const isNativeUser = useIsNativeUser();

  const [isEditing, setIsEditing] = useState(isFormResponseEditMode || false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const translate = useTranslate();
  const { data: formInstance, isLoading: isFormInstanceLoading } =
    useQueryFormInstanceById(instanceId);
  // this ref is used to check if the response has been submitted, if it has then the response shouldn't be saved in the indexedDb
  const isDeletedRef = useRef(false);
  const isEditingRef = useRef(false);

  const { data: formAnswerData, isLoading: isFormAnswerLoading } =
    useFormAnswerQuery(formResponseId ?? -1, {
      enabled: formResponseId !== undefined,
    });

  const [showCopied, setShowCopied] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const formTemplate = formInstance?.formTemplate;
  const isMultiResponseMode =
    formInstance?.formTemplate.formSettings.formResponseType ===
      FormResponseType.MultipleResponse ?? false;

  const formDefaultValues = useMemo(() => {
    if (formAnswerData) {
      return computeFormDefaultValues(formAnswerData.answerMetaJson);
    }

    return undefined;
  }, [formAnswerData]);

  const {
    control,
    setValue: setValueFromHook,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Record<string, string | number | number[]>>({
    defaultValues: formDefaultValues,
    shouldFocusError: false,
  });

  const setValue = useCallback(
    (id, value) => {
      setValueFromHook(id, value);
      isDeletedRef.current = false;
    },
    [setValueFromHook],
  );

  useEffect(() => {
    if (formDefaultValues) {
      reset(formDefaultValues);
    }
  }, [formDefaultValues, reset]);

  useEffect(() => {
    if ((isMultiResponseMode || !formDefaultValues) && formResponseId) {
      // if a multi response form is already submitted, we shouldn't display the values saved in Idb
      return;
    }
    setValuesInForm(
      formInstance?.formInstanceId,
      formAnswerData?.updatedAt,
      formDefaultValues,
      reset,
      setIsEditing,
      setShowBanner,
    );
  }, [
    formAnswerData?.updatedAt,
    formDefaultValues,
    formInstance?.formInstanceId,
    formResponseId,
    isMultiResponseMode,
    reset,
  ]);

  useEffect(() => {
    setTimeout(() => {
      setShowBanner(false);
    }, 4000);
  }, []);

  const flattenedQuestionsMap = useMemo(() => {
    const questions = formInstance?.formTemplate.formQuestions ?? [];

    if (questions) {
      const flattenedQuestions = flattenQuestions(questions);

      return keyBy(flattenedQuestions, 'questionId');
    }

    return {};
  }, [formInstance?.formTemplate.formQuestions]);

  const resetQuestionsToEmptyValues = useCallback(async () => {
    resetQuestionsToEmptyValuesUtil(flattenedQuestionsMap, reset);
    if (formInstance?.formInstanceId != null) {
      if (formResponseId) {
        return;
      }
      deleteFormResponseFromIndexedDb(formInstance?.formInstanceId);
      isDeletedRef.current = true;
    }
  }, [
    flattenedQuestionsMap,
    formInstance?.formInstanceId,
    formResponseId,
    reset,
  ]);

  const redirectToFormResponses = () => {
    const formResponseUrl = RoutePaths.projectFormResponseByInstance
      .replace(':projectId', projectId.toString())
      .replace(':instanceId', instanceId.toString());

    window.open(formResponseUrl, '_blank');
  };

  const onAnswerSubmitSuccess = async (response: FormAnswer) => {
    setIsSubmitLoading(false);
    toastService({
      title: translate('forms.response_submitted'),
      message: isNativeUser ? (
        <ToastDescription
          message={translate('forms.response_submitted_message')}
          buttonText={translate('forms.response.view_responses')}
          onClick={() => redirectToFormResponses()}
        />
      ) : (
        <ToastDescription
          message={translate('forms.response_submitted_message')}
        />
      ),
    }).success();

    if (!(isMultiResponseMode && formResponseId)) {
      if (formInstance?.formInstanceId != null) {
        await deleteFormResponseFromIndexedDb(formInstance?.formInstanceId);
        isDeletedRef.current = true;
      }
    }
    if (isMultiResponseMode) {
      resetQuestionsToEmptyValues();
    } else {
      setIsEditing(false);
      setFormResponseId(response.answerId);
    }

    if (matchPath(pathname, RoutePaths.projectFormResponseByInstance)) {
      await queryClient.invalidateQueries(
        getQueryAllAnswersByFormInstanceKey(instanceId),
      );
    }

    if (
      matchPath(pathname, RoutePaths.formTemplate) &&
      formTemplate?.templateId
    ) {
      await queryClient.invalidateQueries(
        getQueryAllAnswersByFormTemplateKey(formTemplate.templateId),
      );
    }
  };
  const onAnswerSubmitFailure = (error) => {
    const status = error?.response?.status;

    formAnswerSubmissionFailureToast(status, translate);
    setIsSubmitLoading(false);
  };

  const { mutate } = useCreateUpdateFormAnswer({
    taskId,
    projectId,
    onAnswerSubmitSuccess,
    queryConfig: {
      onError: onAnswerSubmitFailure,
    },
  });

  const onSubmit = async (data: FormResponse) => {
    const isAttachmentUploading = validateAttachmentUploading(data);

    if (isAttachmentUploading) {
      toastService({
        title: translate('forms.response.attachment_uploading_title'),
        message: translate('forms.response.attachment_uploading_message'),
      }).info();

      return;
    }
    const formAnswerPayload = computeFormAnswerPayload(
      data,
      flattenedQuestionsMap,
    );

    let submitFormResponse = false;

    const rowErrorObject = Object.values(formAnswerPayload).reduce(
      (acc, curr) => {
        if (curr.type === 'MATRIX') {
          const zeroCount = Object.values(curr.answers).reduce(
            (accumalator, currentValue) => {
              if (currentValue.length === 0) {
                accumalator += 1;
              }

              return accumalator;
            },
            0,
          );
          const responseCount = Object.values(curr.answers).length || 0;
          const parentQuestion = formTemplate
            ? formTemplate.formQuestions.find(
                (question) => question.questionId === curr.questionId,
              )
            : { rows: [] };
          const rowsCount = parentQuestion?.rows?.length || 0;

          if (
            (responseCount !== 0 && responseCount !== rowsCount) ||
            zeroCount > 0
          ) {
            if (
              (parentQuestion as any as FormQuestion)?.required &&
              !submitFormResponse
            ) {
              submitFormResponse = true;
            }

            acc[curr.questionId] = true;
          }
        } else {
          acc[curr.questionId] = false;
        }

        return acc;
      },
      {},
    );

    setRenderRowError(rowErrorObject);
    if (!submitFormResponse) {
      setIsSubmitLoading(true);
      const payload = {
        formInstanceId: instanceId,
        answerMetaJson: formAnswerPayload,
      };

      if (formAnswerData && formResponseId) {
        mutate({
          formAnswerId: formResponseId,
          payload,
        });
      } else {
        mutate({
          payload,
        });
      }
    }
  };

  const onChange = useCallback(
    async (data: FormResponse) => {
      saveFormRespToIndexedDb(
        data,
        isMultiResponseMode,
        instanceId,
        formResponseId,
      );
    },
    [formResponseId, instanceId, isMultiResponseMode],
  );

  useEffect(() => {
    return () => {
      if (
        (isEditingRef.current === true || isMultiResponseMode) &&
        isDeletedRef.current === false
      ) {
        onChange(control._formValues);
      }
    };
  }, [control._formValues, isMultiResponseMode, onChange]);

  const clearFormConfirmation = useDeleteConfirmModal({
    title: `${translate('clear_form')}?`,
    content: translate('forms.response.clear_form_message'),
    okText: translate('clear'),
    onOk: () => {
      resetQuestionsToEmptyValues();
    },
  });
  const cancelEditForm = async () => {
    isDeletedRef.current = true;
    setIsEditing(false);
    if (formInstance?.formInstanceId) {
      await deleteFormResponseFromIndexedDb(formInstance?.formInstanceId);
    }
    reset(formDefaultValues);
  };

  const userId = formAnswerData?.updatedAt
    ? formAnswerData?.updatedBy
    : formAnswerData?.createdBy;

  const lastSubmittedUserName = useGetUserFromIndexedDb(userId)?.firstName;

  const isFormEditAllowed = !isMultiResponseMode && !isEmpty(formAnswerData);
  const showLastEditedByData =
    !isMultiResponseMode && isFormEditAllowed && !isEditing && formAnswerData;

  useEffect(() => {
    if (formResponseId == null) {
      isEditingRef.current = true;
    } else {
      isEditingRef.current = isEditing;
    }
  }, [isEditing, formResponseId, control._formValues]);

  const refreshSave = useCallback(() => {
    if (isDeletedRef.current === false && isEditingRef.current === true) {
      onChange(control._formValues);
    }
  }, [control._formValues, onChange]);

  useFormUnload(refreshSave);

  const projectResource = useProjectAsRBACResource(projectId);

  return (
    <>
      {isFormInstanceLoading ||
      isFormAnswerLoading ||
      !formInstance ||
      !formTemplate ? (
        <LoadingWrapper>
          <Loading />
        </LoadingWrapper>
      ) : (
        <Wrapper>
          {formInstance.shareUrl && (
            <FormSharableWrapper>
              <FormSharableLink>
                <Typography.Text ellipsis={{ tooltip: true }}>
                  {formInstance.shareUrl}
                </Typography.Text>
              </FormSharableLink>
              <FormLinkCopyButton>
                <Tooltip
                  title="null"
                  onVisibleChange={(visible) =>
                    !visible && setShowCopied(false)
                  }
                  mouseLeaveDelay={2}
                  overlayStyle={{
                    display: 'none',
                  }}
                >
                  <Button
                    type="link"
                    icon={showCopied ? <CheckmarkOutline16 /> : <Copy16 />}
                    iconPlacement="right"
                    onClick={() => {
                      if (!showCopied) {
                        navigator.clipboard.writeText(formInstance?.shareUrl);
                        setShowCopied(true);
                      }
                    }}
                  >
                    {showCopied ? (
                      <FormattedMessage id="copied" />
                    ) : (
                      <FormattedMessage id="copy_link" />
                    )}
                  </Button>
                </Tooltip>
              </FormLinkCopyButton>
            </FormSharableWrapper>
          )}
          <FormWrapper>
            {formTemplate.description ? (
              <FormDesc>{formTemplate.description}</FormDesc>
            ) : null}
            <FormElements>
              {formTemplate.formQuestions.length ? (
                formTemplate.formQuestions.map((question) => (
                  <FormQuestionRenderer
                    question={question}
                    control={control}
                    setValue={setValue}
                    errors={{
                      ...errors,
                    }}
                    disabled={isFormEditAllowed && !isEditing}
                    renderRowErrorObject={renderRowError}
                  />
                ))
              ) : (
                <EmptyStateContainer $transparent>
                  <EmptyState
                    image={<EmptyStateIcon icon={faTelescope} />}
                    title={
                      <FormattedMessage id="forms.response.no_question.title" />
                    }
                    message={
                      <FormattedMessage id="forms.response.no_question.message" />
                    }
                    secondaryCTA={null}
                  />
                </EmptyStateContainer>
              )}
            </FormElements>
          </FormWrapper>
          {formTemplate.formQuestions.length ? (
            <FormFooterWrapper>
              <StyledAlert
                showIcon
                type="warning"
                message={<FormattedMessage id="forms.response.not_submitted" />}
                showBanner={showBanner}
              />
              <FormFooterActionsWrapper>
                <FooterLeftGroup>
                  {isFormEditAllowed && isEditing && (
                    <Button onClick={() => cancelEditForm()}>
                      <FormattedMessage id="cancel" />
                    </Button>
                  )}
                  {showLastEditedByData && (
                    <LastEditedBy
                      submittedBy={lastSubmittedUserName}
                      submittedAt={
                        formAnswerData?.updatedAt || formAnswerData?.createdAt
                      }
                    />
                  )}
                  {isMultiResponseMode && !isFormResponseEditMode ? (
                    <>
                      {formResponseCount ? (
                        <Button
                          data-cy="form.responses"
                          type="link"
                          onClick={redirectToFormResponses}
                        >
                          <FormattedMessage
                            id="forms.view_response.response_count"
                            values={{
                              count: formResponseCount,
                            }}
                          />
                        </Button>
                      ) : (
                        <NoResponseText>
                          <FormattedMessage id="forms.no_response" />
                        </NoResponseText>
                      )}
                    </>
                  ) : null}
                </FooterLeftGroup>
                <FooterRightGroup>
                  {isFormEditAllowed && !isEditing ? (
                    <RbacButton
                      type="primary"
                      data-cy="task.pane.form.editForm"
                      onClick={() => {
                        setIsEditing(true);
                        isDeletedRef.current = false;
                      }}
                      rule="ALL_FORM_RESPONSES:WRITE"
                      resourceData={projectResource}
                    >
                      <FormattedMessage id="forms.responses.edit_response" />
                    </RbacButton>
                  ) : (
                    <>
                      <Button
                        type="tertiary"
                        data-cy="task.pane.form.clearForm"
                        onClick={() => clearFormConfirmation()}
                      >
                        <FormattedMessage id="clear_form" />
                      </Button>
                      <Button
                        type="primary"
                        data-cy="task.pane.form.submit"
                        onClick={handleSubmit(onSubmit)}
                        loading={isSubmitLoading}
                      >
                        <FormattedMessage id="submit" />
                      </Button>
                    </>
                  )}
                </FooterRightGroup>
              </FormFooterActionsWrapper>
            </FormFooterWrapper>
          ) : null}
        </Wrapper>
      )}
    </>
  );
};
